import { mapState } from "pinia";

import { OptionCalculationMixin } from "%/dynamic-component-editor/mixins/option-calculation-mixin";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useGeneralStore = (...args) => require("%/dynamic-component-editor/stores/general-store").useGeneralStore(...args);


// Editor-directory clone of the render engine's
// `mixins/production/dynamic-element-mixin`. The editor directory is
// scoped to only itself and the App engine — it must not import from
// the render engine. Option resolution flows through
// `DynamicElement.getCompiledOptionValue` (via the cloned
// `OptionCalculationMixin`) instead of the classes-v2 pipeline.
export const DynamicElementMixin = {
  isDynamicComponentElement: true,
  mixins: [
    OptionCalculationMixin,
  ],
  props: {
    entityId: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    configuration: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    identifier: {
      type: String,
      required: false,
      default: "",
    },
    overrides: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    breakpoint: {
      type: String,
      required: false,
      default: "",
    },
  },
  optionBundles: [],
  defaultElementPanelSettings: {
    displayIcon: "message",
    elementType: "element",
  },
  created() {
    this.assertValidElement();
  },
  mounted() {
    if (this.editMode) {
      const component = this.$el;
      if (component.style) {
        component.style.cursor = "pointer";
      }
    }
  },
  computed: {
    ...mapState(useGeneralStore, ["links"]),
    // Resolves the element's parent DC via the editor's DynamicElement
    // (reads the `dynamicComponent` pointer off the element entity) and
    // hands back the editor's DynamicComponent instance.
    dynamicComponentInstance() {
      return DynamicComponent.get(DynamicElement.get(this.identifier)?.dynamicComponentId);
    },
  },
  methods: {
    assertValidElement() {
      if (this.$options.production) {
        return true;
      }

      const elementPanelSettings = this.$options.elementPanelSettings;
      if (typeof elementPanelSettings !== "object" || elementPanelSettings === null) {
        throw new DynamicComponentError("Dynamic component element must define a valid elementPanelSettings object", {
          componentName: this.$options.name,
        });
      }
      if (
        !Object.prototype.hasOwnProperty.call(elementPanelSettings, "displayIcon")
      ) {
        elementPanelSettings.displayIcon = this.$options.defaultElementPanelSettings.displayIcon;
      }
      if (
        !Object.prototype.hasOwnProperty.call(elementPanelSettings, "elementType")
      ) {
        elementPanelSettings.elementType = this.$options.defaultElementPanelSettings.elementType;
      }
    },
  },
};
